import React, { useRef } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { QuestionIcon } from './icons/QuestionIcon';
import Tippy from './ReactTippy';

export const DndTable = props => (
  <ul>
    <DndProvider backend={HTML5Backend}>{props.children}</DndProvider>
  </ul>
);

export const DragTableItem = ({
  id,
  name,
  type,
  index,
  conferenceDisplay,
  conferenceEditEditable,
  listDisplay,
  moveItem,
  handleInputChange,
  readOnly,
  multipleEditDisplay,
  multipleEditEditable,
  columnConstraint,
}) => {
  const ref = useRef(null);
  // ドロップ（マウスを離す）対応
  const [, drop] = useDrop({
    accept: 'item', // type: 'item' のみ
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      // それ自体の項目と交換しない
      if (dragIndex === hoverIndex) {
        return;
      }
      // 項目の画面位置情報を取得
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      // 項目の垂直方向の中間点を取得
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      // マウスの位置を取得
      const clientOffset = monitor.getClientOffset();
      // 最上までのpxを計算
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      // マウスが項目の半分を超えた時にのみ移動する
      // 下へドラッグ：マウスが項目の下半分に入った時
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      // 上へドラッグ：マウスが項目の上半分に入った時
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      // 動かすアクション
      moveItem(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    item: { type: 'item', id, index },
    canDrag: !readOnly,
    collect: monitor => ({
      isDragging: monitor.isDragging(), //  React-DnD で「ドラッグ中」状態を管理する為、現在使用してないので参考用
    }),
  });
  const opacity = readOnly ? 0 : 1;
  drag(drop(ref));
  return (
    <li className='list-settings-table-item' ref={ref}>
      <div className='list-settings-table-cell list-settings-table-item__icon'>
        <i className='i-drag i-3x' style={{ opacity }} />
      </div>
      <div className='list-settings-table-cell list-settings-table-item__name'>
        {name}
      </div>
      <div className='list-settings-table-cell list-settings-table-item__list-display'>
        {
          <label htmlFor={`list-${type}`} className='checkbox'>
            <input
              id={`list-${type}`}
              type='checkbox'
              name={`list--${type}`}
              checked={listDisplay}
              onChange={e => {
                handleInputChange(e);
              }}
              className='list-settings-table-item__input'
            />
            <i className='i-square i-3x' aria-hidden='true' />
          </label>
        }
      </div>
      <div className='list-settings-table-cell list-settings-table-item__conference-display'>
        {conferenceEditEditable ? (
          <label htmlFor={`conf-${type}`} className='checkbox'>
            <input
              id={`conf-${type}`}
              type='checkbox'
              name={`conf--${type}`}
              checked={conferenceDisplay}
              onChange={e => {
                handleInputChange(e);
              }}
              className='list-settings-table-item__input'
            />
            <i className='i-square i-3x' aria-hidden='true' />
          </label>
        ) : (
          <i className='list-settings__icon list-settings__icon--remove list-settings__icon--remove--editable-screen' />
        )}
      </div>
      <div className='list-settings-table-cell list-settings-table-item__multiple-edit-display'>
        {!multipleEditEditable ? (
          <i className='list-settings__icon list-settings__icon--remove' />
        ) : (
          <label htmlFor={`multiple-edit-${type}`} className='checkbox'>
            <input
              id={`multiple-edit-${type}`}
              type='checkbox'
              name={`multiple-edit--${type}`}
              checked={multipleEditDisplay}
              onChange={e => {
                handleInputChange(e);
              }}
              className='list-settings-table-item__input'
              disabled={columnConstraint}
            />
            <i
              className={
                columnConstraint ? 'i-square-disabled i-3x' : 'i-square i-3x'
              }
              aria-hidden='true'
            />
            {columnConstraint && (
              <div className='list-settings__icon--tooltip'>
                <Tippy
                  duration={10}
                  distance={24}
                  arrow='true'
                  arrowType='sharp'
                  delay={[100, 100]}
                  placement='bottom'
                  followCursor={true}
                  html={
                    <div className={'tippy-tooltip'}>
                      {columnConstraint}の一括編集をする場合、
                      <br />
                      {name}の一括編集チェックボックスを外すことができません。
                    </div>
                  }
                >
                  <QuestionIcon className={'icon--2x question-icon'} />
                </Tippy>
              </div>
            )}
          </label>
        )}
      </div>
    </li>
  );
};

export const PreviewTableItem = ({
  conferenceDisplay,
  listDisplay,
  multipleEditDisplay,
  name,
}) => (
  <li className='list-settings-table-item list-settings-table-item--read-only'>
    <div className='list-settings-table-cell list-settings-table-item__icon' />
    <div className='list-settings-table-cell list-settings-table-item__name'>
      {name}
    </div>
    <div className='list-settings-table-cell list-settings-table-item__list-display'>
      {listDisplay ? (
        <i className='list-settings__icon list-settings__icon--circle' />
      ) : (
        <i className='list-settings__icon list-settings__icon--remove' />
      )}
    </div>
    <div className='list-settings-table-cell list-settings-table-item__conference-display'>
      {conferenceDisplay ? (
        <i className='list-settings__icon list-settings__icon--circle' />
      ) : (
        <i className='list-settings__icon list-settings__icon--remove' />
      )}
    </div>
    <div className='list-settings-table-cell list-settings-table-item__multiple-edit-display'>
      {multipleEditDisplay ? (
        <i className='list-settings__icon list-settings__icon--circle' />
      ) : (
        <i className='list-settings__icon list-settings__icon--remove list-settings__icon--remove--no-space' />
      )}
    </div>
  </li>
);



// WEBPACK FOOTER //
// ./src/components/ListSettingDndTableItem.jsx